import React from 'react'
import { CellClassFunc, ColDef, ColGroupDef, GridApi, IRowNode } from 'ag-grid-community'
import { flowRight, isEqual, isNil } from 'lodash-es'
import {
  IsBestQuote,
  Market,
  NegotiationDetail,
  NegotiationDetailWithMeta,
  NegotiationPackageDetail,
  NegotiationStatus,
  NegotiationType,
  SummaryRowDirection
} from 'api'
import { Locale, Translation } from 'locale'
import { DecisionMakerHelper, getCollateralMarket, getDateEditorFormat, MarketHelper, UnassignedCollectionId } from 'model'
import { NumericInput, TextInput } from 'view/components/inputs'
import {
  AssignableNameCellRenderer,
  axeTradeConditionKeyCreator,
  createActionsRenderer,
  createAxeTradeConditionRenderer,
  createMasterDetailRowColDef,
  createNestedColumnMapper,
  dateFilterParams,
  isNegotiationOrAuditDetailBestQuote,
  StandardGridColumnId,
  StatusCellRenderer,
  tradesGridGroupingRowColDef,
  TypedHeaderValueGetterParams,
  TypedRowNode,
  TypedValueGetterParams,
  ValidationCellRenderer,
  withAllContext
} from 'view/components/datagrid'
import {
  axeFilterValueFormatter,
  axeTradeConditionFilterTextGetter,
  booleanFormatter,
  callableTypeFormatter,
  canEditInitiatedNegotiationDetailStartConsiderationOverride,
  canEditNegotiationDetailBondPrice,
  canEditNegotiationDetailEndDate,
  canEditNegotiationDetailFee,
  canEditNegotiationDetailHaircut,
  canEditNegotiationDetailHaircutType,
  canEditNegotiationDetailIdmEdm,
  canEditNegotiationDetailNotional,
  canEditNegotiationDetailRate,
  canEditNegotiationDetailRFQType,
  canEditNegotiationDetailSecondaryCollateral,
  canEditNegotiationDetailStartConsideration,
  canEditNegotiationPreAgreed,
  canEditNegotiationsDetailBookStrategy,
  canEditNegotiationStartDate,
  canEditNegotiationTerm,
  collectionFilterFormatter,
  collectionFormatter,
  containerKeyCreator,
  counterPartyNameFormatter,
  dateQuickFilterTextGetter,
  evergreenNoticeFormatter,
  evergreenNoticePeriodQuickfixFilterTextGetter,
  getFeeFormatOptions,
  getHaircutFormatOptions,
  getNegotiationClearingStatusForNonSummary,
  getNotionalFormatOptions,
  getOwnDirection,
  getRateFormatOptions,
  getSettlementVenueName,
  getStartConsiderationFormatOptions,
  getTargetValueIfNotOcoSummary,
  getTargetValueIfNotSummary,
  groupedStandardCollectionKeyCreator,
  haircutTypeFormatter,
  hasNegotiationNestedLegs,
  isMultiDirectionSummary,
  makeCusipGetter,
  makeDateFormatter,
  makeExecutingTradingEntityNameGetter,
  makeFeeFormatter,
  makeHaircutFilterGetter,
  makeHaircutFormatter,
  makeHaircutQuickFilterTextGetter,
  makeIsinGetter,
  makeNotionalFilterGetter,
  makeNotionalFormatter,
  makeNumberFilterGetter,
  makeNumberFormatter,
  makeNumberFormatterSummaryRow,
  makeNumberQuickFilterTextGetter,
  makeOrganisationFieldGetter,
  makePrevRateFilterGetter,
  makeRateFilterGetter,
  makeRateFormatter,
  makeRateQuickFilterTextGetter,
  makeSponsorNameGetter,
  makeStartConsiderationFilterGetter,
  makeStartConsiderationFormatter,
  makeTimeFormatter,
  makeTradingEntityFieldGetter,
  makeTradingEntityLeiGetter,
  makeTradingEntityNameGetter,
  negotiationRowDataGetter,
  NOTE_INDEXES,
  notionalHeaderValueGetter,
  orderTermFormatter,
  orderTermQuickFilterTextGetter,
  preTradeOrTradingEntityNameGetter,
  repoClassificationFormatter,
  rfqTypeFormatter,
  timeQuickFilterTextGetter
} from 'view/shared/datagrid'
import { BooleanSelectbox, DecisionMakerSelectbox, FilteredTermSelectbox, RFQTypeSelectbox, TradingDatePicker } from 'view/shared/entity'
import {
  getBondPriceModificationType,
  getBooleanModificationType,
  getEndDateDelta,
  getEndDateModificationType,
  getHaircutDelta,
  getHaircutModificationType,
  getHaircutTypeModificationType,
  getNotionalDelta,
  getNotionalModificationType,
  getRateModificationType,
  getRFQTypeModificationType,
  getStartConsiderationDelta,
  getStartConsiderationModificationType,
  getStartConsiderationOverrideModificationType,
  getStartDateDelta,
  getStartDateModificationType,
  getStringModificationType,
  getTermModificationType
} from 'view/routes/requests/shared/deltaGetters'
import {
  canSend,
  getBestPriceRowNodes,
  getBestQuoteReceived,
  getLiveStatusColor,
  getNegotiatedValue,
  getNegotiationPackageResponseCount,
  getNegotiationStatusAsString,
  hasMatchingQuote,
  isBestPriceStatusSelected
} from 'view/routes/requests/shared/utils'
import { LiveGridColumnId, NegotiationFieldNames } from '../LiveNegotiationsGrid/LiveGridColumnNames'
import { ClearingGridColumnId, CollateralBasketColumnId, CollectionColumnId, ContainerCollectionColumnId } from 'view/routes/ColumnNames'
import { DeltaGroupHeaderComponent } from '../LiveNegotiationsGrid/DeltaGroupHeaderComponent'
import { headerTemplateDelta } from '../LiveNegotiationsGrid/headerTemplates'
import { LiveActionsCell } from '../LiveNegotiationsGrid/LiveActionsCell'
import { TimeRenderer } from '../LiveNegotiationsGrid/TimeRenderer'
import { NegotiableCellRenderer } from '../LiveNegotiationsGrid/NegotiableCellRenderer'
import { LiveAxeTradeConditionButtons } from '../LiveNegotiationsGrid/LiveAxeTradeConditionButtons'
import { createCounterpartyActionsRenderer } from 'view/components/datagrid/renderers/CounterpartyActionsRenderer'
import { withCounterpartyNegotiation } from 'view/components/datagrid/withCounterpartyNegotiation'
import { createCounterpartyCellRenderer } from 'view/components/datagrid/renderers/CounterpartyCellRenderer'
import { AxeRollUnwindSummaryComponent } from '../LiveNegotiationsGrid/AxeRollUnwindSummaryComponent'
import { BackToStagingAllNegotiationsButton } from 'view/components/datagrid/toolbar/BackToStagingAllNegotiationsButton'
import { getNegotiationDetailsForBestPriceComparison, NEGOTIATIONS_PATH } from 'view/components/datagrid/renderers/negotiationDataUtil'
import { initiatedNegotiationDetailsToTopLevelDetailsTransformerSelector } from 'selectors/negotiations'
import * as constants from 'view/styles/constants'
import { HaircutTypeSelectbox } from 'view/shared/entity/haircutOptions/HaircutTypeSelectbox'
import { FilteredCheckboxHeaderRenderer } from 'view/components/datagrid/renderers/FilteredCheckboxHeaderRenderer'
import { swallowPrecisionError } from 'utils/Decimal'
import { BackToStagingAllOrGroupedActionsRenderer } from 'view/components/datagrid/toolbar/BackToStagingAllOrGroupedActionsRenderer'
import { InitiatedSecondaryCollateralCombobox } from 'view/shared/entity/SecondaryCollateralSelectbox'
import { NegotiationCheckboxRenderer } from '../LiveNegotiationsGrid/NegotiationActionRenderer/NegotiationCheckboxRenderer'
import { NegotiationStatusRendererComponent } from '../LiveNegotiationsGrid/NegotiationActionRenderer/NegotiationStatusRenderer'
import { NegotiationTimeRendererComponent } from '../LiveNegotiationsGrid/NegotiationActionRenderer/NegotiationTimeRenderer'
import { isNotNil } from 'utils/predicate'
import { iconSet } from 'view/components/icons'
import { InitiatedCollectionNameLink } from './InitiatedCollectionNameLink'
import { translate } from 'locale/Locale'
import { StagingGridColumnId } from 'view/routes/staging/StagingOrdersGrid/StagingGridColumnNames'
import {
  BASE_ASSET_TYPE,
  BASE_COLLECTION_TYPE,
  BASE_DIRECTION,
  BASE_ISSUE,
  BASE_MARKET_TYPE,
  BASE_MIC,
  BASE_REPO_TYPE,
  BASE_ROE,
  BASE_ROS
} from 'view/shared/datagrid/baseColumns'

const ALL_POSSIBLE_STATUSES = [
  Translation.TC_ALL,
  Translation.TC_READY_TO_SEND,
  Translation.TC_PRICED,
  Translation.TC_AWAITING_RESPONSE,
  Translation.TC_REFRESHED,
  Translation.TC_COUNTERED,
  Translation.TC_QUOTE_SUBJECT,
  Translation.TC_REJECTED,
  Translation.TC_EXPIRED,
  Translation.TC_WITHDRAWN,
  Translation.TC_NONE,
  Translation.TC_BEST_PRICE
].map(translation => Locale.translate(translation))

const CHECKBOX: ColDef = {
  colId: StandardGridColumnId.CHECKBOX,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  headerComponent: FilteredCheckboxHeaderRenderer,
  headerComponentParams: {
    allPossibleStatuses: ALL_POSSIBLE_STATUSES,
    getStatusAsString: getNegotiationStatusAsString,
    getDerivedStatuses: function getBestPriceStatus(gridApi: GridApi) {
      const bestPriceNodes = getBestPriceRowNodes(gridApi)

      if (bestPriceNodes.length) return [Locale.translate(Translation.TC_BEST_PRICE)]
      return []
    },
    getDerivedStatusesRowNodes: function handleBestPriceSelection(key: string, gridApi: GridApi): IRowNode[] {
      if (key === Locale.translate(Translation.TC_BEST_PRICE)) {
        return getBestPriceRowNodes(gridApi)
      }

      return []
    },
    getSelectedDerivedStatus: (gridApi: GridApi): string | null => {
      const isBestPriceSelected = isBestPriceStatusSelected(gridApi)

      if (isBestPriceSelected) return Locale.translate(Translation.TC_BEST_PRICE)
      return null
    },
    dataTestId: 'InitiatedSelectAll',
    withPortal: true
  },
  checkboxSelection: true,
  width: 75,
  maxWidth: 75,
  minWidth: 75,
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockPosition: true,
  lockVisible: true,
  filter: false,
  resizable: false,
  sortable: false
}

const ACTIONS: ColDef = {
  colId: StandardGridColumnId.ACTIONS,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  equals: () => false,
  width: 210,
  minWidth: 210,
  headerComponent: withAllContext(BackToStagingAllOrGroupedActionsRenderer),
  headerComponentParams: {
    dataTestId: 'InitiatedHeaderActions',
    withPortal: true
  },
  cellClass: 'action-cell',
  cellRenderer: withAllContext(
    createActionsRenderer(LiveActionsCell, negotiationRowDataGetter, initiatedNegotiationDetailsToTopLevelDetailsTransformerSelector)
  ),
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockPosition: true,
  lockVisible: true,
  filter: false,
  sortable: false
}

const initiatedCollectionLabelRenderer = (label: string, leafChildren: IRowNode[]) => {
  const collectionId =
    label === translate(Translation.TC_COLLECTION_UNASSIGNED)
      ? UnassignedCollectionId
      : leafChildren[0]?.data?.containerCollection?.id ?? leafChildren[0]?.data?.collection?.id

  return <InitiatedCollectionNameLink label={label} collectionId={collectionId} />
}

const GROUPING: ColDef = {
  ...tradesGridGroupingRowColDef,
  cellRendererParams: {
    ...tradesGridGroupingRowColDef.cellRendererParams,
    columnLabelRenderers: {
      [CollectionColumnId.COLLECTION_NAME]: initiatedCollectionLabelRenderer,
      [ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME]: initiatedCollectionLabelRenderer
    },
    columnIcons: {
      ...tradesGridGroupingRowColDef.cellRendererParams.columnIcons,
      [LiveGridColumnId.COUNTERPARTY_NAME]: (
        _groupName: string,
        _leafChildren: IRowNode<NegotiationDetailWithMeta>[],
        params: TypedValueGetterParams<NegotiationDetailWithMeta>
      ) => {
        const rowData = negotiationRowDataGetter(params)

        if (rowData?.meta?.isBestQuote === IsBestQuote.YES) {
          return iconSet.starIcon
        }

        return undefined
      }
    }
  }
}

const COLLECTION_NAME: ColDef = {
  colId: CollectionColumnId.COLLECTION_NAME,
  headerName: Locale.translate(Translation.TC_COLLECTION_NAME),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collection,
  keyCreator: groupedStandardCollectionKeyCreator,
  valueFormatter: collectionFormatter,
  cellRenderer: AssignableNameCellRenderer,
  cellRendererParams: {
    unassignedLabel: Locale.translate(Translation.TC_COLLECTION_UNASSIGNED)
  },
  width: 150,
  filter: 'agSetColumnFilter',
  filterParams: { valueFormatter: collectionFilterFormatter },
  hide: true
}

const CONTAINER_COLLECTION_NAME: ColDef = {
  colId: ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME,
  headerName: Locale.translate(Translation.TC_CONTAINER_COLLECTION_NAME),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.containerCollection,
  keyCreator: containerKeyCreator,
  valueFormatter: collectionFormatter,
  cellRenderer: AssignableNameCellRenderer,
  cellRendererParams: {
    unassignedLabel: Locale.translate(Translation.TC_COLLECTION_UNASSIGNED),
    hideUnassignedLabel: true,
    dataTestId: 'StagingOrdersGridModel-ContainerCollectionName-AssignableNameCellRenderer'
  },
  width: 150,
  filter: 'agSetColumnFilter',
  filterParams: {
    valueFormatter: collectionFilterFormatter
  },
  hide: true
}

const COLLECTION_TYPE: ColDef = {
  ...BASE_COLLECTION_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collection?.type,
  hide: true
}

const COLLECTION_ID: ColDef = {
  colId: CollectionColumnId.COLLECTION_ID,
  headerName: Locale.translate(Translation.TC_COLLECTION_ID),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collection?.id,
  filter: 'agSetColumnFilter',
  width: 150,
  hide: true
}

const CONTAINER_COLLECTION_ID: ColDef = {
  colId: ContainerCollectionColumnId.CONTAINER_COLLECTION_ID,
  headerName: Locale.translate(Translation.TC_CONTAINER_COLLECTION_ID),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.containerCollection?.id,
  filter: 'agSetColumnFilter',
  width: 150,
  hide: true
}

const OTW_DURATION: ColDef = {
  colId: LiveGridColumnId.OTW_DURATION,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.negotiation.status === NegotiationStatus.QUOTED ? rowData.negotiation.otwEndTime : rowData?.otwDuration
  },
  headerName: Locale.translate(Translation.TC_OTW_TIME),
  equals: () => false,
  width: 84,
  minWidth: 84,
  cellRenderer: withAllContext(TimeRenderer),
  cellRendererParams: {
    which: 'otw'
  },
  lockPinned: false,
  enableRowGroup: false
}

const NEGOTIATION_ID: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_ID,
  headerName: Locale.translate(Translation.TC_NEGOTIATION_ID),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.negotiationId,
  width: 120,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_AXE_ID: ColDef<NegotiationDetail> = {
  colId: LiveGridColumnId.NEGOTIATION_AXE_ID,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.axeId,
  headerName: Locale.translate(Translation.TC_AXE_ID),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false,
  hide: true
}

const RFQ_TIMER: ColDef = {
  colId: LiveGridColumnId.RFQ_TIMER,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.rfqEndTime,
  headerName: Locale.translate(Translation.TC_RFQ_TIME),
  equals: () => false,
  width: 84,
  minWidth: 84,
  cellRenderer: withAllContext(TimeRenderer),
  cellRendererParams: {
    which: 'rfq'
  },
  enableRowGroup: false
}

const NEGOTIATION_STATUS: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_STATUS,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData ? getNegotiationStatusAsString(rowData) : ''
  },
  headerName: Locale.translate(Translation.TC_STATUS),
  width: 140,
  filter: 'agSetColumnFilter',
  cellRenderer: StatusCellRenderer,
  cellRendererParams: {
    rowDataGetter: negotiationRowDataGetter,
    getStatusColor: (data: NegotiationDetail) => (canSend(data) ? constants.STATUS_COLOR_READY : getLiveStatusColor(data))
  },
  equals: () => false,
  lockVisible: true
}

const FUND_NAME: ColDef = {
  colId: LiveGridColumnId.FUND_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && preTradeOrTradingEntityNameGetter(true, rowData)
  },
  headerName: Locale.translate(Translation.TC_MY_ENTITY),
  width: 150,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY_CLEARING: ColDef = {
  colId: ClearingGridColumnId.COUNTERPARTY_CLEARING,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getNegotiationClearingStatusForNonSummary(rowData)
  },
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_CLEARED),
  width: 70,
  filter: 'agSetColumnFilter',
  hide: true
}

const COUNTERPARTY_NAME: ColDef = {
  colId: LiveGridColumnId.COUNTERPARTY_NAME,
  valueGetter: makeTradingEntityNameGetter(false, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY),
  width: 150,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation, clearingHouse }: NegotiationDetail) =>
      isNil(clearingHouse) || negotiation.market === Market.US
        ? getStringModificationType(negotiation.counterpartyIdNegotiableField)
        : null,
    validationField: NegotiationFieldNames.COUNTERPARTY_NAME,
    rowDataGetter: negotiationRowDataGetter
  },
  rowGroup: true
}

const COUNTERPARTY_SPONSOR: ColDef = {
  colId: ClearingGridColumnId.COUNTERPARTY_SPONSOR,
  valueGetter: makeSponsorNameGetter(true, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_SPONSOR),
  width: 170,
  filter: 'agSetColumnFilter',
  hide: true
}

const EXECUTING_COUNTERPARTY_NAME: ColDef = {
  colId: ClearingGridColumnId.EXECUTING_COUNTERPARTY_NAME,
  valueGetter: makeExecutingTradingEntityNameGetter(negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY),
  width: 170,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation, clearingHouse }: NegotiationDetail) =>
      !isNil(clearingHouse) && negotiation.market !== Market.US
        ? getStringModificationType(negotiation.counterpartyIdNegotiableField)
        : null,
    validationField: NegotiationFieldNames.COUNTERPARTY_NAME,
    rowDataGetter: negotiationRowDataGetter
  },
  hide: true
}

const NEGOTIATION_LAST_RESPONDER: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_LAST_RESPONDER,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastResponderId,
  headerName: Locale.translate(Translation.TC_LAST_RESPONDER),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_UPDATED_AT_DATE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_UPDATED_AT_DATE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastUpdatedAt,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_DATE),
  width: 130,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const NEGOTIATION_UPDATED_AT_TIME: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_UPDATED_AT_TIME,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastUpdatedAt,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_TIME),
  width: 130,
  filter: false,
  valueFormatter: makeTimeFormatter(),
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const NEGOTIATION_RFQ_TYPE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_RFQ_TYPE,
  headerName: Locale.translate(Translation.TC_RFQ_TYPE),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.rfqType))
  },
  valueFormatter: rfqTypeFormatter,
  width: 100,
  filter: 'agSetColumnFilter',
  equals: () => false,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getRFQTypeModificationType(negotiation.rfqType),
    validationField: NegotiationFieldNames.RFQ_TYPE,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: RFQTypeSelectbox,
    inputParams: () => ({
      withPortal: true
    }),
    rowDataGetter: negotiationRowDataGetter,
    getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
    defaultField: NegotiationFieldNames.RFQ_TYPE_NEGOTIATED,
    updateField: NegotiationFieldNames.RFQ_TYPE_MODIFIED,
    isModifiedField: NegotiationFieldNames.RFQ_TYPE_IS_MODIFIED,
    validationField: NegotiationFieldNames.RFQ_TYPE
  },
  editable: canEditNegotiationDetailRFQType,
  lockPinned: true
}

const NEGOTIATION_DIRECTION: ColDef = {
  ...BASE_DIRECTION,
  colId: LiveGridColumnId.NEGOTIATION_DIRECTION,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    if (!rowData) {
      return null
    }
    if (isMultiDirectionSummary(rowData)) {
      return SummaryRowDirection.MULTIPLE
    }
    return rowData && getOwnDirection(rowData.negotiation.direction, rowData.ownOrder)
  },
  headerName: Locale.translate(Translation.TC_ABBREVIATED_DIRECTION),
  cellRendererParams: {
    rowDataGetter: negotiationRowDataGetter
  }
}

const COLLATERAL_NAME: ColDef = {
  colId: LiveGridColumnId.COLLATERAL_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.collateral?.name ?? rowData?.collateralBasket?.name
  },
  headerName: Locale.translate(Translation.TC_COLLATERAL),
  width: 120,
  filter: 'agSetColumnFilter'
}

const EVERGREEN_NOTICE_PERIOD: ColDef = {
  colId: LiveGridColumnId.EVERGREEN_NOTICE_PERIOD,
  headerName: Locale.translate(Translation.TC_EVERGREEN_NOTICE_PERIOD),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.negotiation?.evergreenNoticePeriod
  },
  valueFormatter: evergreenNoticeFormatter,
  getQuickFilterText: evergreenNoticePeriodQuickfixFilterTextGetter,
  width: 120,
  filter: 'agSetColumnFilter',
  enableRowGroup: false,
  hide: true
}

const COLLATERAL_BASKET_DESCRIPTION: ColDef = {
  colId: CollateralBasketColumnId.COLLATERAL_BASKET_DESCRIPTION,
  headerName: Locale.translate(Translation.TC_BASKET_DESCRIPTION),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collateralBasket?.description,
  width: 220,
  filter: 'agSetColumnFilter',
  hide: true
}

const COLLATERAL_BASKET_DBV_CLASS: ColDef = {
  colId: CollateralBasketColumnId.COLLATERAL_BASKET_DBV_CLASS,
  headerName: Locale.translate(Translation.TC_DBV_CLASS),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collateralBasket?.dbvClass,
  width: 120,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_NOTIONAL_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      colId: LiveGridColumnId.NEGOTIATION_NOTIONAL,
      headerValueGetter: notionalHeaderValueGetter,
      headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.notional))
      },
      equals: () => false,
      width: 95,
      minWidth: 95,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell',
      headerComponent: DeltaGroupHeaderComponent,
      headerComponentParams: {
        children: [LiveGridColumnId.NEGOTIATION_NOTIONAL_ORIGINAL, LiveGridColumnId.NOTIONAL_DELTA]
      },
      valueFormatter: makeNotionalFormatter(false, negotiationRowDataGetter),
      filterValueGetter: makeNotionalFilterGetter(),
      cellRenderer: NegotiableCellRenderer,
      cellRendererParams: {
        getModificationType: ({ negotiation }: NegotiationDetail) => getNotionalModificationType(negotiation.notional),
        validationField: NegotiationFieldNames.NOTIONAL,
        rowDataGetter: negotiationRowDataGetter
      },
      editable: canEditNegotiationDetailNotional,
      cellEditorParams: {
        inputComponent: NumericInput,
        inputParams: (negotiationDetail: NegotiationDetail, context: any) => ({
          min: negotiationDetail.collateral?.ticketIncrement,
          step: negotiationDetail.collateral?.ticketIncrement,
          formatOptions: getNotionalFormatOptions(context.userPreference.amountFormat)
        }),
        defaultField: NegotiationFieldNames.NOTIONAL_NEGOTIATED,
        updateField: NegotiationFieldNames.NOTIONAL_MODIFIED,
        isModifiedField: NegotiationFieldNames.NOTIONAL_IS_MODIFIED,
        validationField: NegotiationFieldNames.NOTIONAL
      },
      lockPinned: true,
      enableRowGroup: false,
      cellClassRules: {
        'paren-on-negative-cell': (params: any) => params.node.group && params.value < 0
      }
    },
    {
      colId: LiveGridColumnId.NEGOTIATION_NOTIONAL_ORIGINAL,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotSummary(rowData, rowData?.negotiation.notional.original)
      },
      headerValueGetter: (params: TypedHeaderValueGetterParams<any>) =>
        Locale.translate(Translation.TC_ORIGINAL, { amountFormat: params.context.userPreference.amountFormat }),
      headerName: Locale.translate(Translation.TC_ORIGINAL_SHORT),
      width: 115,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        parent: LiveGridColumnId.NEGOTIATION_NOTIONAL
      },
      valueFormatter: makeNotionalFormatter(false, negotiationRowDataGetter),
      filterValueGetter: makeNotionalFilterGetter(),
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    },
    {
      colId: LiveGridColumnId.NOTIONAL_DELTA,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotOcoSummary(rowData, getNotionalDelta(rowData?.negotiation.notional))
      },
      headerValueGetter: notionalHeaderValueGetter,
      headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
      width: 110,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        template: headerTemplateDelta,
        parent: LiveGridColumnId.NEGOTIATION_NOTIONAL,
        delta: true
      },
      valueFormatter: makeNotionalFormatter(true, negotiationRowDataGetter),
      filterValueGetter: makeNotionalFilterGetter(true),
      getQuickFilterText: makeNumberQuickFilterTextGetter({ sign: true }),
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    }
  ]
}

const NEGOTIATION_NOTIONAL_ORIGINAL_COMMON: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_NOTIONAL_ORIGINAL,
  headerName: Locale.translate(Translation.TC_NOTIONAL_ORIGINAL_SHORT),
  headerValueGetter: (params: TypedHeaderValueGetterParams<any>) =>
    Locale.translate(Translation.TC_NOTIONAL_ORIGINAL, { amountFormat: params.context.userPreference.amountFormat }),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.notional.original)
  },
  width: 140,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNotionalFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeNotionalFilterGetter(),
  enableRowGroup: false
}

const NEGOTIATION_RATE_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      colId: LiveGridColumnId.NEGOTIATION_RATE,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.rate))
      },
      cellClassRules: {
        'ag-cell--best-negotiation': params => {
          const rowData = negotiationRowDataGetter(params, true)
          return !params.context?.userPreference?.counterPartyGridView && isNotNil(rowData) && isNegotiationOrAuditDetailBestQuote(rowData)
        }
      },
      headerName: Locale.translate(Translation.TC_RATE),
      equals: () => false,
      width: 80,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell',
      headerComponent: DeltaGroupHeaderComponent,
      headerComponentParams: {
        children: [LiveGridColumnId.NEGOTIATION_RATE_TARGET, LiveGridColumnId.NEGOTIATION_LAST_QUOTED]
      },
      valueFormatter: makeRateFormatter(false, negotiationRowDataGetter),
      filterValueGetter: makeRateFilterGetter(),
      getQuickFilterText: makeRateQuickFilterTextGetter(),
      cellRenderer: NegotiableCellRenderer,
      cellRendererParams: {
        getModificationType: ({ negotiation }: NegotiationDetail) => getRateModificationType(negotiation.rate),
        validationField: NegotiationFieldNames.RATE,
        isRateField: true,
        rowDataGetter: negotiationRowDataGetter
      },
      editable: canEditNegotiationDetailRate,
      cellEditorParams: {
        inputComponent: NumericInput,
        inputParams: (negotiationDetail: NegotiationDetail) => ({
          formatOptions: getRateFormatOptions(negotiationDetail.referenceRate),
          updateOnBlurOnly: negotiationDetail.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY,
          dataTestId: 'LiveNegotiationsGridModel-RateNumericInput'
        }),
        rowDataGetter: negotiationRowDataGetter,
        getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
        defaultField: NegotiationFieldNames.RATE_NEGOTIATED,
        updateField: NegotiationFieldNames.RATE_MODIFIED,
        isModifiedField: NegotiationFieldNames.RATE_IS_MODIFIED,
        validationField: NegotiationFieldNames.RATE
      },
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: LiveGridColumnId.NEGOTIATION_RATE_TARGET,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotSummary(rowData, rowData?.negotiation.rate.original)
      },
      headerName: Locale.translate(Translation.TC_TARGET),
      width: 80,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        parent: LiveGridColumnId.NEGOTIATION_RATE
      },
      valueFormatter: makeRateFormatter(false, negotiationRowDataGetter),
      filterValueGetter: makeRateFilterGetter(),
      getQuickFilterText: makeRateQuickFilterTextGetter(),
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    },
    {
      colId: LiveGridColumnId.NEGOTIATION_LAST_QUOTED,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.lastQuoted)
      },
      headerName: Locale.translate(Translation.TC_LAST_QUOTED),
      width: 100,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        parent: LiveGridColumnId.NEGOTIATION_RATE
      },
      valueFormatter: makeRateFormatter(false, negotiationRowDataGetter),
      filterValueGetter: makeRateFilterGetter(),
      getQuickFilterText: makeRateQuickFilterTextGetter(),
      enableRowGroup: false,
      hide: true
    }
  ]
}

const NEGOTIATION_TERM: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_TERM,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.term))
  },
  headerName: Locale.translate(Translation.TC_TERM),
  equals: () => false,
  width: 120,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getTermModificationType(negotiation.term),
    validationField: NegotiationFieldNames.TERM,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: FilteredTermSelectbox,
    inputParams: (negotiationDetail: NegotiationDetail) => ({
      repoClassification: negotiationDetail.negotiation.repoClassification,
      withPortal: true
    }),
    rowDataGetter: negotiationRowDataGetter,
    getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
    defaultField: NegotiationFieldNames.TERM_NEGOTIATED,
    updateField: NegotiationFieldNames.TERM_MODIFIED,
    isModifiedField: NegotiationFieldNames.TERM_IS_MODIFIED,
    validationField: NegotiationFieldNames.TERM
  },
  valueFormatter: orderTermFormatter,
  getQuickFilterText: orderTermQuickFilterTextGetter,
  editable: canEditNegotiationTerm,
  lockPinned: true,
  enableRowGroup: true
}

const NEGOTIATION_START_DATE_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      colId: LiveGridColumnId.NEGOTIATION_START_DATE,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.startDate))
      },
      headerName: Locale.translate(Translation.TC_START_DATE),
      equals: () => false,
      width: 120,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      headerClass: 'group-header-cell',
      headerComponent: DeltaGroupHeaderComponent,
      headerComponentParams: {
        children: [LiveGridColumnId.NEGOTIATION_START_DATE_TARGET, LiveGridColumnId.START_DATE_DELTA]
      },
      valueFormatter: makeDateFormatter(),
      getQuickFilterText: dateQuickFilterTextGetter,
      cellRenderer: NegotiableCellRenderer,
      cellRendererParams: {
        getModificationType: ({ negotiation }: NegotiationDetail) => getStartDateModificationType(negotiation.startDate),
        validationField: NegotiationFieldNames.START_DATE,
        rowDataGetter: negotiationRowDataGetter
      },
      cellEditorParams: {
        inputComponent: TradingDatePicker,
        inputParams: (negotiationDetail: NegotiationDetail, context: any) => ({
          market: negotiationDetail.collateral && getCollateralMarket(negotiationDetail.collateral),
          currency: negotiationDetail.negotiation.currency,
          dateFormat: getDateEditorFormat(context.userPreference.dateFormat),
          withPortal: true
        }),
        rowDataGetter: negotiationRowDataGetter,
        getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
        defaultField: NegotiationFieldNames.START_DATE_NEGOTIATED,
        updateField: NegotiationFieldNames.START_DATE_MODIFIED,
        isModifiedField: NegotiationFieldNames.START_DATE_IS_MODIFIED,
        validationField: NegotiationFieldNames.START_DATE
      },
      editable: canEditNegotiationStartDate,
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: LiveGridColumnId.NEGOTIATION_START_DATE_TARGET,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotSummary(rowData, rowData?.negotiation.startDate.original)
      },
      headerName: Locale.translate(Translation.TC_TARGET),
      width: 120,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      headerClass: 'group-header-cell',
      headerComponentParams: {
        parent: LiveGridColumnId.NEGOTIATION_START_DATE
      },
      valueFormatter: makeDateFormatter(),
      getQuickFilterText: dateQuickFilterTextGetter,
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    },
    {
      colId: LiveGridColumnId.START_DATE_DELTA,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotOcoSummary(rowData, getStartDateDelta(rowData?.negotiation.startDate))
      },
      headerName: Locale.translate(Translation.TC_START_DATE),
      width: 120,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      headerClass: 'group-header-cell',
      headerComponentParams: {
        template: headerTemplateDelta,
        parent: LiveGridColumnId.NEGOTIATION_START_DATE,
        delta: true
      },
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    }
  ]
}

const NEGOTIATION_END_DATE_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      colId: LiveGridColumnId.NEGOTIATION_END_DATE,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.endDate))
      },
      headerName: Locale.translate(Translation.TC_END_DATE),
      equals: () => false,
      width: 120,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      headerClass: 'group-header-cell',
      headerComponent: DeltaGroupHeaderComponent,
      headerComponentParams: {
        children: [LiveGridColumnId.NEGOTIATION_END_DATE_TARGET, LiveGridColumnId.END_DATE_DELTA]
      },
      valueFormatter: makeDateFormatter(),
      getQuickFilterText: dateQuickFilterTextGetter,
      cellRenderer: NegotiableCellRenderer,
      cellRendererParams: {
        getModificationType: ({ negotiation }: NegotiationDetail) => getEndDateModificationType(negotiation.endDate),
        validationField: NegotiationFieldNames.END_DATE,
        rowDataGetter: negotiationRowDataGetter
      },
      cellEditorParams: {
        inputComponent: TradingDatePicker,
        inputParams: (negotiationDetail: NegotiationDetail, context: any) => ({
          market: negotiationDetail.collateral && getCollateralMarket(negotiationDetail.collateral),
          currency: negotiationDetail.negotiation.currency,
          dateFormat: getDateEditorFormat(context.userPreference.dateFormat),
          withPortal: true
        }),
        rowDataGetter: negotiationRowDataGetter,
        getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
        defaultField: NegotiationFieldNames.END_DATE_NEGOTIATED,
        updateField: NegotiationFieldNames.END_DATE_MODIFIED,
        isModifiedField: NegotiationFieldNames.END_DATE_IS_MODIFIED,
        validationField: NegotiationFieldNames.END_DATE
      },
      editable: canEditNegotiationDetailEndDate,
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: LiveGridColumnId.NEGOTIATION_END_DATE_TARGET,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotSummary(rowData, rowData?.negotiation.endDate.original)
      },
      headerName: Locale.translate(Translation.TC_TARGET),
      width: 120,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      headerClass: 'group-header-cell',
      headerComponentParams: {
        parent: LiveGridColumnId.NEGOTIATION_END_DATE
      },
      valueFormatter: makeDateFormatter(),
      getQuickFilterText: dateQuickFilterTextGetter,
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    },
    {
      colId: LiveGridColumnId.END_DATE_DELTA,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotOcoSummary(rowData, getEndDateDelta(rowData?.negotiation.endDate))
      },
      headerName: Locale.translate(Translation.TC_END_DATE),
      width: 120,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      headerClass: 'group-header-cell',
      headerComponentParams: {
        template: headerTemplateDelta,
        parent: LiveGridColumnId.NEGOTIATION_END_DATE,
        delta: true
      },
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    }
  ]
}

const NEGOTIATION_HAIRCUT_TYPE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_HAIRCUT_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.haircutType))
  },
  valueFormatter: haircutTypeFormatter,
  headerName: Locale.translate(Translation.TC_COLLATERAL_HAIRCUT),
  equals: () => false,
  width: 150,
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.HAIRCUT_TYPE,
    rowDataGetter: negotiationRowDataGetter,
    getModificationType: ({ negotiation }: NegotiationDetail) => getHaircutTypeModificationType(negotiation.haircutType),
    isHaircutOrHaircutTypeField: true
  },
  cellEditorParams: {
    inputComponent: HaircutTypeSelectbox,
    inputParams: (_negotiationDetail: NegotiationDetail) => ({
      canClear: true,
      withPortal: true
    }),
    rowDataGetter: negotiationRowDataGetter,
    getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
    defaultField: NegotiationFieldNames.HAIRCUT_TYPE_NEGOTIATED,
    updateField: NegotiationFieldNames.HAIRCUT_TYPE_MODIFIED,
    isModifiedField: NegotiationFieldNames.HAIRCUT_TYPE_IS_MODIFIED,
    validationField: NegotiationFieldNames.HAIRCUT_TYPE
  },
  editable: canEditNegotiationDetailHaircutType,
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_HAIRCUT_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      colId: LiveGridColumnId.NEGOTIATION_HAIRCUT,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.haircut))
      },
      headerName: Locale.translate(Translation.TC_HAIRCUT),
      equals: () => false,
      width: 80,
      minWidth: 80,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell',
      headerComponent: DeltaGroupHeaderComponent,
      headerComponentParams: {
        children: [LiveGridColumnId.NEGOTIATION_HAIRCUT_TARGET, LiveGridColumnId.HAIRCUT_DELTA]
      },
      valueFormatter: makeHaircutFormatter(),
      filterValueGetter: makeHaircutFilterGetter(),
      getQuickFilterText: makeHaircutQuickFilterTextGetter(),
      cellRenderer: NegotiableCellRenderer,
      cellRendererParams: {
        getModificationType: ({ negotiation }: NegotiationDetail) => getHaircutModificationType(negotiation.haircut),
        validationField: NegotiationFieldNames.HAIRCUT,
        rowDataGetter: negotiationRowDataGetter,
        dataTestId: 'LiveNegotiationsGridModel-HaircutNegotiableCellRenderer',
        isHaircutOrHaircutTypeField: true
      },
      editable: canEditNegotiationDetailHaircut,
      cellEditorParams: {
        inputComponent: NumericInput,
        inputParams: (negotiationDetail: NegotiationDetail, context: any) => ({
          formatOptions: getHaircutFormatOptions(
            undefined,
            context.userPreference.haircutSign,
            negotiationDetail.ownOrder,
            negotiationDetail.direction
          ),
          updateOnBlurOnly: negotiationDetail.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY,
          dataTestId: 'LiveNegotiationsGridModel-HaircutNumericInput'
        }),
        rowDataGetter: negotiationRowDataGetter,
        getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
        defaultField: NegotiationFieldNames.HAIRCUT_NEGOTIATED,
        updateField: NegotiationFieldNames.HAIRCUT_MODIFIED,
        isModifiedField: NegotiationFieldNames.HAIRCUT_IS_MODIFIED,
        validationField: NegotiationFieldNames.HAIRCUT
      },
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: LiveGridColumnId.NEGOTIATION_HAIRCUT_TARGET,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotSummary(rowData, rowData?.negotiation.haircut.original)
      },
      headerName: Locale.translate(Translation.TC_TARGET),
      equals: () => false,
      width: 80,
      minWidth: 80,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        parent: LiveGridColumnId.NEGOTIATION_HAIRCUT
      },
      valueFormatter: makeHaircutFormatter(),
      filterValueGetter: makeHaircutFilterGetter(),
      getQuickFilterText: makeHaircutQuickFilterTextGetter(),
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    },
    {
      colId: LiveGridColumnId.HAIRCUT_DELTA,
      valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
        const rowData = negotiationRowDataGetter(params)
        return getTargetValueIfNotOcoSummary(rowData, getHaircutDelta(rowData?.negotiation.haircut))
      },
      headerName: Locale.translate(Translation.TC_HAIRCUT),
      equals: () => false,
      width: 80,
      minWidth: 80,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        template: headerTemplateDelta,
        parent: LiveGridColumnId.NEGOTIATION_HAIRCUT,
        delta: true
      },
      valueFormatter: makeHaircutFormatter(true),
      filterValueGetter: makeHaircutFilterGetter(true),
      getQuickFilterText: makeHaircutQuickFilterTextGetter(true),
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    }
  ]
}

const NEGOTIATION_CALLABLE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_CALLABLE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.callableType,
  headerName: Locale.translate(Translation.TC_CALLABLE),
  width: 100,
  filter: 'agSetColumnFilter',
  valueFormatter: callableTypeFormatter,
  hide: true
}

const NEGOTIATION_CALL_DAYS: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_CALL_DAYS,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.callablePeriodDays,
  headerName: Locale.translate(Translation.TC_CALL_DAYS),
  width: 100,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  enableRowGroup: false,
  hide: true
}

const NEGOTIATION_BOND_PRICE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_BOND_PRICE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.bondPrice))
  },
  headerName: Locale.translate(Translation.TC_COLLATERAL_PRICE),
  equals: () => false,
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({
    maxFractionDigits: constants.bondPriceMaxFractionDigits
  }),
  filterValueGetter: makeNumberFilterGetter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getBondPriceModificationType(negotiation.bondPrice),
    validationField: NegotiationFieldNames.BOND_PRICE,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: canEditNegotiationDetailBondPrice,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: () => ({
      min: 0,
      maxLength: constants.bondPriceMaxFieldLength,
      formatOptions: {
        maxFractionDigits: constants.bondPriceMaxFractionDigits
      }
    }),
    defaultField: NegotiationFieldNames.BOND_PRICE_NEGOTIATED,
    updateField: NegotiationFieldNames.BOND_PRICE_MODIFIED,
    isModifiedField: NegotiationFieldNames.BOND_PRICE_IS_MODIFIED,
    validationField: NegotiationFieldNames.BOND_PRICE
  },
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_ALL_IN_PRICE = {
  colId: LiveGridColumnId.NEGOTIATION_ALL_IN_PRICE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotSummary(rowData, rowData.negotiation.allInPrice)
  },
  headerName: Locale.translate(Translation.TC_ALL_IN_PRICE),
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  filterValueGetter: makeNumberFilterGetter(),
  enableRowGroup: false
}

const NEGOTIATION_ACCRUED_INTEREST: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_ACCRUED_INTEREST,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.accruedInterest)
  },
  headerName: Locale.translate(Translation.TC_COLLATERAL_ACCRUED_INTEREST),
  width: 180,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatterSummaryRow({ minDp: 2 }, negotiationRowDataGetter),
  enableRowGroup: false
}

const NEGOTIATION_START_CONSIDERATION = {
  colId: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.startConsideration))
  },
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
  equals: () => false,
  width: 90,
  minWidth: 90,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeStartConsiderationFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeStartConsiderationFilterGetter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: swallowPrecisionError(({ negotiation }: NegotiationDetail) =>
      getStartConsiderationModificationType(negotiation.startConsideration)
    ),
    validationField: NegotiationFieldNames.START_CONSIDERATION,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: canEditNegotiationDetailStartConsideration,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: (negotiationDetail: NegotiationDetail) => ({
      min: negotiationDetail.collateral?.ticketIncrement,
      step: negotiationDetail.collateral?.ticketIncrement,
      formatOptions: getStartConsiderationFormatOptions()
    }),
    defaultField: NegotiationFieldNames.START_CONSIDERATION_NEGOTIATED,
    updateField: NegotiationFieldNames.START_CONSIDERATION_MODIFIED,
    isModifiedField: NegotiationFieldNames.START_CONSIDERATION_IS_MODIFIED,
    validationField: NegotiationFieldNames.START_CONSIDERATION
  },
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_START_CONSIDERATION_ORIGINAL = {
  colId: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_ORIGINAL,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.startConsideration.original)
  },
  headerName: Locale.translate(Translation.TC_TARGET),
  width: 115,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  headerClass: 'group-header-cell group-header-cell-right-align',
  headerComponentParams: {
    parent: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION
  },
  valueFormatter: makeStartConsiderationFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeStartConsiderationFilterGetter(),
  enableRowGroup: false
}

const START_CONSIDERATION_DELTA = {
  colId: LiveGridColumnId.START_CONSIDERATION_DELTA,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotOcoSummary(rowData, getStartConsiderationDelta(rowData?.negotiation.startConsideration))
  },
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
  width: 110,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  headerClass: 'group-header-cell group-header-cell-right-align',
  headerComponentParams: {
    template: headerTemplateDelta,
    parent: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION,
    delta: true
  },
  valueFormatter: makeStartConsiderationFormatter(true, negotiationRowDataGetter),
  filterValueGetter: makeStartConsiderationFilterGetter(true),
  getQuickFilterText: makeNumberQuickFilterTextGetter({ sign: true }),
  enableRowGroup: false
}

const NEGOTIATION_START_CONSIDERATION_OVERRIDE = {
  colId: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_OVERRIDE,
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)

    if (rowData && hasNegotiationNestedLegs(rowData)) {
      return null
    }

    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.startConsiderationOverride))
  },
  valueFormatter: booleanFormatter,
  equals: () => false,
  width: 120,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: swallowPrecisionError(({ negotiation }: NegotiationDetail) =>
      getStartConsiderationOverrideModificationType(negotiation.startConsiderationOverride)
    ),
    validationField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: BooleanSelectbox,
    inputParams: () => ({
      withPortal: true
    }),
    defaultField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_NEGOTIATED,
    updateField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_MODIFIED,
    isModifiedField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_IS_MODIFIED,
    validationField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE
  },
  editable: canEditInitiatedNegotiationDetailStartConsiderationOverride,
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_START_CONSIDERATION_GROUP_CPTY: ColGroupDef = {
  marryChildren: true,
  children: [NEGOTIATION_START_CONSIDERATION_ORIGINAL, START_CONSIDERATION_DELTA, NEGOTIATION_START_CONSIDERATION_OVERRIDE]
}

const NEGOTIATION_START_CONSIDERATION_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      ...NEGOTIATION_START_CONSIDERATION,
      headerClass: 'group-header-cell',
      headerComponent: DeltaGroupHeaderComponent,
      headerComponentParams: {
        children: [
          LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_ORIGINAL,
          LiveGridColumnId.START_CONSIDERATION_DELTA,
          LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_OVERRIDE
        ]
      }
    },
    { ...NEGOTIATION_START_CONSIDERATION_ORIGINAL, hide: true },
    { ...START_CONSIDERATION_DELTA, hide: true },
    { ...NEGOTIATION_START_CONSIDERATION_OVERRIDE, hide: true }
  ]
}

const ABSOLUTE_CONSIDERATION: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_ABSOLUTE_CONSIDERATION,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.absoluteConsideration,
  headerName: Locale.translate(Translation.TC_ABSOLUTE_CONSIDERATION),
  width: 70,
  minWidth: 70,
  headerClass: 'group-header-cell group-header-cell-right-align',
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  enableRowGroup: false,
  hide: true
}

const NEGOTIATION_REPO_INTEREST: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_REPO_INTEREST,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.repoInterest)
  },
  headerName: Locale.translate(Translation.TC_REPO_INTEREST),
  width: 70,
  minWidth: 70,
  headerClass: 'group-header-cell group-header-cell-right-align',
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }, true),
  enableRowGroup: false,
  hide: true,
  cellClassRules: {
    'paren-on-negative-cell': (params: any) => params.node.group && params.value < 0
  }
}

const NEGOTIATION_END_CONSIDERATION: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_END_CONSIDERATION,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.endConsideration)
  },
  headerName: Locale.translate(Translation.TC_END_CONSIDERATION),
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatterSummaryRow({ minDp: 2 }, negotiationRowDataGetter),
  enableRowGroup: false
}

const COLLATERAL_ISIN_OR_BUI: ColDef = {
  colId: LiveGridColumnId.COLLATERAL_ISIN_OR_BUI,
  valueGetter: makeIsinGetter(negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_ISIN_BUI),
  width: 140,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY_FIRM: ColDef = {
  colId: LiveGridColumnId.COUNTERPARTY_FIRM,
  valueGetter: makeOrganisationFieldGetter('legalName', false, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const COLLATERAL_CURRENCY: ColDef = {
  colId: LiveGridColumnId.COLLATERAL_CURRENCY,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.currency,
  headerName: Locale.translate(Translation.TC_CURRENCY),
  width: 90,
  filter: 'agSetColumnFilter'
}

const REFERENCE_RATE_NAME: ColDef = {
  colId: LiveGridColumnId.REFERENCE_RATE_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.referenceRate?.name,
  headerName: Locale.translate(Translation.TC_REFERENCE_RATE),
  width: 120,
  filter: 'agSetColumnFilter',
  hide: true
}

const FLOAT_CRYSTALLISATION_DAYS = {
  colId: LiveGridColumnId.FLOAT_CRYSTALLISATION_DAYS,
  headerName: Locale.translate(Translation.TC_FLOAT_CRYSTALLISATION_DAYS),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.floatCrystallisationDays,
  width: 120,
  filter: 'agSetColumnFilter',
  equals: () => false,
  editable: false,
  lockPinned: true
}

const NEGOTIATION_SETTLEMENT_VENUE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_SETTLEMENT_VENUE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getSettlementVenueName(getNegotiatedValue(rowData.negotiation.settlementVenueNegotiable))
  },
  headerName: Locale.translate(Translation.TC_SETTLEMENT),
  equals: () => false,
  width: 150,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getStringModificationType(negotiation.settlementVenueNegotiable),
    validationField: NegotiationFieldNames.SETTLEMENT_VENUE,
    rowDataGetter: negotiationRowDataGetter
  },
  hide: true,
  enableRowGroup: true
}

const NEGOTIATION_CSD: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_CSD,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.csd,
  headerName: Locale.translate(Translation.TC_CSD),
  width: 100,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_AXE_ROLL_UNWIND: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_AXE_ROLL_UNWIND,
  headerName: Locale.translate(Translation.TC_AXE_ROLL_UNWIND),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.axeTradeCondition,
  equals: () => false,
  cellRenderer: withAllContext(createAxeTradeConditionRenderer(LiveAxeTradeConditionButtons)),
  keyCreator: axeTradeConditionKeyCreator,
  getQuickFilterText: axeTradeConditionFilterTextGetter,
  valueFormatter: axeTradeConditionFilterTextGetter,
  width: 130,
  filter: 'agSetColumnFilter',
  filterParams: { valueFormatter: axeFilterValueFormatter },
  sortable: false
}

const NEGOTIATION_REPO_TYPE: ColDef = {
  ...BASE_REPO_TYPE,
  colId: LiveGridColumnId.NEGOTIATION_REPO_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.repoType
}

const NEGOTIATION_TRADE_TYPE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_TRADE_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.repoClassification)
  },
  headerName: Locale.translate(Translation.TC_TRADE_TYPE),
  width: 100,
  filter: 'agSetColumnFilter',
  valueFormatter: repoClassificationFormatter,
  hide: true
}

const PRODUCT: ColDef = {
  colId: LiveGridColumnId.PRODUCT,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.product?.name,
  headerName: Locale.translate(Translation.TC_PRODUCT),
  width: 80,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_ORDER_ID: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_ORDER_ID,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.parentOrderId)
  },
  headerName: Locale.translate(Translation.TC_RFQ_ID),
  width: 100,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_FACTOR: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_FACTOR,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.factor)
  },
  headerName: Locale.translate(Translation.TC_FACTOR),
  width: 100,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_ASSET_TYPE: ColDef = {
  ...BASE_ASSET_TYPE,
  colId: LiveGridColumnId.NEGOTIATION_ASSET_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.assetType)
  },
  width: 100,
  hide: true
}

const NEGOTIATION_LAST_INITIATOR: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_LAST_INITIATOR,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastInitiatorId,
  headerName: Locale.translate(Translation.TC_LAST_INITIATOR),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_CREATED_AT_DATE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_CREATED_AT_DATE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.createdAt,
  headerName: Locale.translate(Translation.TC_CREATION_DATE),
  width: 110,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const SUBMITTING_USER: ColDef = {
  colId: LiveGridColumnId.SUBMITTING_USER,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.createdBy,
  headerName: Locale.translate(Translation.TC_SUBMITTING_USER),
  width: 250,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_CREATED_AT_TIME: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_CREATED_AT_TIME,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.createdAt,
  headerName: Locale.translate(Translation.TC_CREATION_TIME),
  width: 110,
  filter: false,
  valueFormatter: makeTimeFormatter(),
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const NEGOTIATION_UPDATED_BY: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_UPDATED_BY,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastUpdatedBy,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
  width: 130,
  filter: 'agSetColumnFilter',
  hide: true
}

const INVESTMENT_DECISION_MAKER = {
  colId: LiveGridColumnId.INVESTMENT_DECISION_MAKER,
  headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.initiatorInvestmentDecisionMaker && DecisionMakerHelper.getName(rowData.initiatorInvestmentDecisionMaker)
  },
  equals: () => false,
  width: 180,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: DecisionMakerSelectbox,
    inputParams: () => ({
      withPortal: true
    }),
    defaultField: NegotiationFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_NEGOTIATED,
    updateField: NegotiationFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_MODIFIED,
    isModifiedField: NegotiationFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_IS_MODIFIED,
    validationField: NegotiationFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER
  },
  editable: canEditNegotiationDetailIdmEdm,
  lockPinned: true,
  enableRowGroup: false
}

const EXECUTION_DECISION_MAKER = {
  colId: LiveGridColumnId.EXECUTION_DECISION_MAKER,
  headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.initiatorExecutionDecisionMaker && DecisionMakerHelper.getName(rowData.initiatorExecutionDecisionMaker)
  },
  equals: () => false,
  width: 180,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.INITIATOR_EXECUTION_DECISION_MAKER,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: DecisionMakerSelectbox,
    inputParams: () => ({
      withPortal: true
    }),
    defaultField: NegotiationFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_NEGOTIATED,
    updateField: NegotiationFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_MODIFIED,
    isModifiedField: NegotiationFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_IS_MODIFIED,
    validationField: NegotiationFieldNames.INITIATOR_EXECUTION_DECISION_MAKER
  },
  editable: canEditNegotiationDetailIdmEdm,
  lockPinned: true,
  enableRowGroup: false
}

const CLEARING_HOUSE: ColDef = {
  colId: ClearingGridColumnId.CLEARING_VENUE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.clearingHouse?.publishedName
  },
  headerName: Locale.translate(Translation.TC_CLEARING_VENUE),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const COUNTERPARTY_LEI: ColDef = {
  colId: LiveGridColumnId.COUNTERPARTY_LEI,
  valueGetter: makeTradingEntityLeiGetter(false, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_LEI),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const COUNTERPARTY_BIC: ColDef = {
  colId: LiveGridColumnId.COUNTERPARTY_BIC,
  valueGetter: makeTradingEntityFieldGetter('bic', false, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_BIC),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const COUNTERPARTY_FIRM_LEI: ColDef = {
  colId: LiveGridColumnId.COUNTERPARTY_FIRM_LEI,
  valueGetter: makeOrganisationFieldGetter('lei', false, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM_LEI),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const ORDER_PREV_RATE: ColDef = {
  colId: StagingGridColumnId.ORDER_PREV_RATE,
  headerName: Locale.translate(Translation.TC_PREV_RATE),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
    negotiationRowDataGetter(params)?.negotiation.servicingTrigger?.previousRate,
  valueFormatter: makeRateFormatter(),
  filterValueGetter: makePrevRateFilterGetter(),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const COLLATERAL_COUPON: ColDef = {
  colId: LiveGridColumnId.COLLATERAL_COUPON,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collateral?.coupon,
  headerName: Locale.translate(Translation.TC_COUPON),
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ percent: true, minDp: 1, decimalShift: 2 }),
  filterValueGetter: makeNumberFilterGetter({ percent: true, minDp: 1, decimalShift: 2 }),
  enableRowGroup: false,
  hide: true
}

const BOOK_NAME: ColDef = {
  colId: LiveGridColumnId.BOOK_NAME,
  headerName: Locale.translate(Translation.TC_BOOK),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.initiatorBook))
  },
  equals: () => false,
  width: 180,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.INITIATOR_BOOK,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: TextInput,
    inputParams: () => ({
      maxLength: constants.internalBookingMaxFieldLength
    }),
    defaultField: NegotiationFieldNames.INITIATOR_BOOK_NEGOTIATED,
    updateField: NegotiationFieldNames.INITIATOR_BOOK_MODIFIED,
    isModifiedField: NegotiationFieldNames.INITIATOR_BOOK_IS_MODIFIED,
    validationField: NegotiationFieldNames.INITIATOR_BOOK
  },
  editable: canEditNegotiationsDetailBookStrategy,
  lockPinned: true,
  enableRowGroup: false,
  hide: true
}

const STRATEGY_NAME: ColDef = {
  colId: LiveGridColumnId.STRATEGY_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.initiatorStrategy))
  },
  headerName: Locale.translate(Translation.TC_STRATEGY),
  equals: () => false,
  width: 180,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.INITIATOR_STRATEGY,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: TextInput,
    inputParams: () => ({
      maxLength: constants.internalBookingMaxFieldLength
    }),
    defaultField: NegotiationFieldNames.INITIATOR_STRATEGY_NEGOTIATED,
    updateField: NegotiationFieldNames.INITIATOR_STRATEGY_MODIFIED,
    isModifiedField: NegotiationFieldNames.INITIATOR_STRATEGY_IS_MODIFIED,
    validationField: NegotiationFieldNames.INITIATOR_STRATEGY
  },
  editable: canEditNegotiationsDetailBookStrategy,
  lockPinned: true,
  enableRowGroup: false,
  hide: true
}

const INITIATOR_PRIME_BROKER_NAME: ColDef = {
  colId: LiveGridColumnId.PRIME_BROKER_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
    negotiationRowDataGetter(params)?.negotiation.initiatorPrimeBrokerName,
  headerName: Locale.translate(Translation.TC_PRIME_BROKER),
  width: 180,
  filter: 'agSetColumnFilter',
  hide: true
}

const BUNDLE_NAME: ColDef = {
  colId: LiveGridColumnId.BUNDLE_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.bundle,
  headerName: Locale.translate(Translation.TC_BUNDLE),
  width: 180,
  filter: 'agSetColumnFilter',
  hide: true
}

const COLLATERAL_ISSUER: ColDef = {
  colId: LiveGridColumnId.COLLATERAL_ISSUER,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collateral?.issuer,
  headerName: Locale.translate(Translation.TC_ISSUER),
  width: 80,
  filter: 'agSetColumnFilter',
  hide: true
}

const BLOCK_TRADE: ColDef = {
  colId: LiveGridColumnId.BLOCK_TRADE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.blockTrade) ?? undefined
  },
  headerName: Locale.translate(Translation.TC_BLOCK),
  width: 80,
  filter: 'agSetColumnFilter',
  valueFormatter: booleanFormatter,
  hide: true
}

const ALLOW_PARTIAL: ColDef = {
  colId: LiveGridColumnId.ALLOW_PARTIAL,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.allowPartial) ?? undefined
  },
  headerName: Locale.translate(Translation.TC_ALLOW_PARTIAL),
  width: 80,
  filter: 'agSetColumnFilter',
  valueFormatter: booleanFormatter,
  hide: true
}

const TOTAL_FILL_NOTIONAL = {
  colId: LiveGridColumnId.NEGOTIATION_TOTAL_FILL_NOTIONAL,
  headerName: Locale.translate(Translation.TC_TOTAL_FILL_NOTIONAL),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.totalFillNotional,
  equals: () => false,
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNotionalFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeNotionalFilterGetter(),
  hide: true
}

const TARGET_FILL_NOTIONAL = {
  colId: LiveGridColumnId.NEGOTIATION_TARGET_FILL_NOTIONAL,
  headerName: Locale.translate(Translation.TC_TARGET_FILL_NOTIONAL),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.targetFillNotional,
  equals: () => false,
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNotionalFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeNotionalFilterGetter(),
  hide: true
}

const TOTAL_FILL_START_CONSIDERATION = {
  colId: LiveGridColumnId.NEGOTIATION_TOTAL_FILL_START_CONSIDERATION,
  headerName: Locale.translate(Translation.TC_TOTAL_FILL_START_CONSIDERATION),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
    negotiationRowDataGetter(params)?.negotiation.totalFillStartConsideration,
  equals: () => false,
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeStartConsiderationFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeStartConsiderationFilterGetter(),
  hide: true
}

const TARGET_FILL_START_CONSIDERATION = {
  colId: LiveGridColumnId.NEGOTIATION_TARGET_FILL_START_CONSIDERATION,
  headerName: Locale.translate(Translation.TC_TARGET_FILL_START_CONSIDERATION),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
    negotiationRowDataGetter(params)?.negotiation.targetFillStartConsideration,
  equals: () => false,
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeStartConsiderationFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeStartConsiderationFilterGetter(),
  hide: true
}

const FUND_LEI: ColDef = {
  colId: LiveGridColumnId.FUND_LEI,
  valueGetter: makeTradingEntityLeiGetter(true, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_MY_ENTITY_LEI),
  width: 170,
  filter: 'agSetColumnFilter',
  hide: true
}

const FUND_BIC: ColDef = {
  colId: LiveGridColumnId.FUND_BIC,
  valueGetter: makeTradingEntityFieldGetter('bic', true, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_MY_ENTITY_BIC),
  width: 170,
  filter: 'agSetColumnFilter',
  hide: true
}

const FUND_FIRM: ColDef = {
  colId: LiveGridColumnId.FUND_FIRM,
  valueGetter: makeOrganisationFieldGetter('legalName', true, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_MY_FIRM),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const FUND_FIRM_LEI: ColDef = {
  colId: LiveGridColumnId.FUND_FIRM_LEI,
  valueGetter: makeOrganisationFieldGetter('lei', true, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_MY_FIRM_LEI),
  width: 170,
  filter: 'agSetColumnFilter',
  hide: true
}

const COLLATERAL_CUSIP: ColDef = {
  colId: LiveGridColumnId.COLLATERAL_CUSIP,
  valueGetter: makeCusipGetter(negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_CUSIP),
  width: 120,
  filter: 'agSetColumnFilter',
  hide: true
}

const PRODUCT_SECTOR: ColDef = {
  colId: LiveGridColumnId.PRODUCT_SECTOR,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.productSector?.name,
  headerName: Locale.translate(Translation.TC_SECTOR),
  width: 100,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_MARKET: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_MARKET,
  headerName: Locale.translate(Translation.TC_MARKET),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const market = negotiationRowDataGetter(params)?.negotiation.market
    return market && MarketHelper.getName(market)
  },
  width: 80,
  filter: 'agSetColumnFilter',
  hide: true
}

const NOTICE_PERIOD_DAYS: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_NOTICE_PERIOD_DAYS,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.noticePeriodDays,
  headerName: Locale.translate(Translation.TC_NOTICE_PERIOD_DAYS),
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  enableRowGroup: false,
  hide: true
}

const NEGOTIATION_PRE_AGREED: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_PRE_AGREED,
  headerName: Locale.translate(Translation.TC_PRE_AGREED),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.preAgreed))
  },
  valueFormatter: booleanFormatter,
  equals: () => false,
  width: 120,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getBooleanModificationType(negotiation.preAgreed),
    validationField: NegotiationFieldNames.PRE_AGREED,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: BooleanSelectbox,
    inputParams: () => ({
      withPortal: true
    }),
    defaultField: NegotiationFieldNames.PRE_AGREED_NEGOTIATED,
    updateField: NegotiationFieldNames.PRE_AGREED_MODIFIED,
    isModifiedField: NegotiationFieldNames.PRE_AGREED_IS_MODIFIED,
    validationField: NegotiationFieldNames.PRE_AGREED
  },
  editable: canEditNegotiationPreAgreed,
  lockPinned: true,
  enableRowGroup: false,
  hide: true
}

const NEGOTIATION_MASTER_LEGAL_AGREEMENT: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_MASTER_LEGAL_AGREEMENT,
  headerName: Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENT),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotSummary(rowData, rowData.negotiation.masterLegalAgreementType)
  },
  width: 180,
  filter: 'agSetColumnFilter',
  hide: true
}

const BEST_QUOTE_GROUP: ColGroupDef = {
  headerName: Locale.translate(Translation.TC_BEST_QUOTE_RECEIVED),
  headerClass: 'header-group-cell--best-quote',
  marryChildren: true,
  children: [
    {
      colId: LiveGridColumnId.BEST_COUNTERPARTY,
      valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
        const negotiationsToCompare = getNegotiationDetailsForBestPriceComparison(params)
        if (!negotiationsToCompare) {
          return null
        }

        const tradingEntityNameGetter = makeTradingEntityNameGetter(false)
        const data = getBestQuoteReceived(negotiationsToCompare)

        const hasMatching = data ? hasMatchingQuote(data, negotiationsToCompare, params.context.bestPriceType) : false

        const entityName =
          data &&
          tradingEntityNameGetter({
            ...params,
            data,
            node: {
              ...params.node,
              data
            } as TypedRowNode<NegotiationDetail>
          })
        return entityName && `${entityName}${hasMatching ? ' (+)' : ''}`
      },
      headerName: Locale.translate(Translation.TC_COUNTERPARTY),
      width: 100,
      filter: 'agSetColumnFilter',
      editable: false,
      cellClass: 'ag-cell--best-quote-group',
      headerClass: 'group-header-cell--best-quote'
    },
    {
      colId: LiveGridColumnId.NEGOTIATION_TERM,
      valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
        const negotiationsToCompare = getNegotiationDetailsForBestPriceComparison(params)
        if (!negotiationsToCompare) {
          return null
        }
        const bestQuote = getBestQuoteReceived(negotiationsToCompare)
        return bestQuote && getNegotiatedValue(bestQuote.negotiation.term)
      },
      headerName: Locale.translate(Translation.TC_TERM),
      equals: () => false,
      width: 120,
      filter: 'agSetColumnFilter',
      cellRenderer: NegotiableCellRenderer,
      cellRendererParams: {
        getModificationType: ({ negotiation }: NegotiationDetail) => getTermModificationType(negotiation.term),
        validationField: NegotiationFieldNames.TERM,
        rowDataGetter: negotiationRowDataGetter
      },
      valueFormatter: orderTermFormatter,
      getQuickFilterText: orderTermQuickFilterTextGetter,
      editable: false,
      enableRowGroup: true
    },
    {
      colId: LiveGridColumnId.BEST_NEGOTIATION_RATE,
      valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
        const negotiationsToCompare = getNegotiationDetailsForBestPriceComparison(params)
        if (!negotiationsToCompare) {
          return null
        }
        const bestQuote = getBestQuoteReceived(negotiationsToCompare)
        return bestQuote && getNegotiatedValue(bestQuote.negotiation.rate)
      },
      headerName: Locale.translate(Translation.TC_RATE),
      equals: () => false,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align group-header-cell--best-quote',
      valueFormatter: makeRateFormatter(false, negotiationRowDataGetter),
      filterValueGetter: makeRateFilterGetter(),
      getQuickFilterText: makeRateQuickFilterTextGetter(),
      editable: false,
      cellClass: 'ag-numeric-cell ag-cell--best-quote-group',
      width: 80
    },
    {
      colId: LiveGridColumnId.BEST_HAIRCUT,
      valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
        const negotiationsToCompare = getNegotiationDetailsForBestPriceComparison(params)
        if (!negotiationsToCompare) {
          return null
        }
        const bestQuote = getBestQuoteReceived(negotiationsToCompare)
        return bestQuote && getNegotiatedValue(bestQuote.negotiation.haircut)
      },
      headerName: Locale.translate(Translation.TC_HAIRCUT),
      equals: () => false,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      valueFormatter: makeHaircutFormatter(),
      filterValueGetter: makeHaircutFilterGetter(),
      getQuickFilterText: makeHaircutQuickFilterTextGetter(),
      editable: false,
      cellClass: 'ag-numeric-cell ag-cell--best-quote-group',
      width: 70
    },
    {
      colId: LiveGridColumnId.BEST_NEGOTIATION_REPO_INTEREST,
      valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
        const negotiationsToCompare = getNegotiationDetailsForBestPriceComparison(params)
        if (!negotiationsToCompare) {
          return null
        }
        const bestQuote = getBestQuoteReceived(negotiationsToCompare)
        return bestQuote && bestQuote.negotiation.repoInterest
      },
      headerName: Locale.translate(Translation.TC_REPO_INTEREST),
      equals: () => false,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      valueFormatter: makeNumberFormatter({ minDp: 2 }, true),
      editable: false,
      cellClass: 'ag-numeric-cell ag-cell--best-quote-group',
      width: 70
    }
  ]
}

const COUNTERPARTY_RESPONSE_COUNT: ColDef = {
  colId: LiveGridColumnId.COUNTERPARTY_RESPONSE_COUNT,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    if (!params.data?.[NEGOTIATIONS_PATH]) {
      return null
    }
    const negotiations = Object.values(params.data[NEGOTIATIONS_PATH])
    return `(${getNegotiationPackageResponseCount(negotiations)}/${negotiations.length})`
  },
  headerName: Locale.translate(Translation.TC_COUNT),
  width: 55,
  minWidth: 55,
  equals: () => false,
  cellClass: 'ag-cell--pivoted-view-count'
}

const COUNTERPARTY_AXE_ROLL_UNWIND_SUMMARY: ColDef = {
  colId: LiveGridColumnId.COUNTERPARTY_AXE_ROLL_UNWIND_SUMMARY,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    if (params.data?.negotiations === undefined) {
      return []
    }

    const counterpartyTradeConditionArr = Object.keys(params.data?.negotiations).map(counterpartyId => {
      const negotiation = params.data?.negotiations[counterpartyId]

      return {
        publishedName: negotiation?.counterparty?.publishedName,
        isAxed: negotiation?.axeTradeCondition.isAxed,
        tradeCondition: negotiation?.axeTradeCondition.tradeCondition
      }
    })

    return counterpartyTradeConditionArr
  },
  headerName: Locale.translate(Translation.TC_AXE_ROLL_UNWIND_SUMMARY),
  valueFormatter: counterPartyNameFormatter,
  getQuickFilterText: counterPartyNameFormatter,
  width: 92,
  minWidth: 92,
  cellRenderer: AxeRollUnwindSummaryComponent
}

const COUNTERPARTY_CHECKBOX: ColDef = {
  colId: StandardGridColumnId.CHECKBOX,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  headerClass: 'ag-header-cell--grouped-actions',
  cellClass: 'ag-cell--pivoted-view-actions',
  cellRenderer: withAllContext(NegotiationCheckboxRenderer),
  equals: () => false,
  width: 50,
  minWidth: 50,
  sortable: false,
  enableRowGroup: false,
  suppressMovable: true,
  editable: false,
  singleClickEdit: false
}

const COUNTERPARTY_NEGOTIATION_STATUS: ColDef<NegotiationDetail> = {
  colId: LiveGridColumnId.NEGOTIATION_STATUS,
  headerName: Locale.translate(Translation.TC_STATUS),
  headerClass: 'ag-header-cell--grouped-actions',
  cellRenderer: NegotiationStatusRendererComponent,
  equals: () => false,
  width: 90,
  minWidth: 90,
  enableRowGroup: false,
  suppressMovable: true,
  editable: false,
  singleClickEdit: false
}

const COUNTERPARTY_NEGOTIATION_TIMER: ColDef = {
  colId: LiveGridColumnId.OTW_DURATION,
  headerName: Locale.translate(Translation.TC_OTW),
  headerClass: 'ag-header-cell--grouped-actions',
  cellRenderer: NegotiationTimeRendererComponent,
  cellRendererParams: {
    which: 'otw'
  },
  equals: () => false,
  width: 90,
  minWidth: 90,
  enableRowGroup: false,
  suppressMovable: true,
  editable: false,
  singleClickEdit: false
}

const COUNTERPARTY_ACTIONS: ColDef = {
  ...ACTIONS,
  headerComponent: withAllContext(BackToStagingAllNegotiationsButton),
  cellRenderer: flowRight(withAllContext, withCounterpartyNegotiation, createCounterpartyActionsRenderer)(LiveActionsCell),
  width: 225,
  minWidth: 225,
  maxWidth: 225
}

const OTW_TIMER_SELECTED_COUNTERPARTY: ColDef = {
  colId: LiveGridColumnId.OTW_TIMER_SELECTED_COUNTERPARTY,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => params.data?.otwDuration,
  headerName: Locale.translate(Translation.TC_OTW_TIME),
  equals: () => false,
  width: 84,
  minWidth: 84,
  cellRenderer: flowRight(withAllContext, withCounterpartyNegotiation, createCounterpartyCellRenderer)(TimeRenderer),
  cellRendererParams: {
    which: 'otw'
  },
  enableRowGroup: false
}
const COLLATERAL_CCY: ColDef = {
  ...COLLATERAL_CURRENCY,
  headerName: Locale.translate(Translation.TC_CCY),
  width: 50,
  maxWidth: 50
}

const makeNoteColumn = (index: string) => ({
  colId: `${LiveGridColumnId.NOTES}_${index}`,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.notes?.values[index],
  headerName: Locale.translate(Translation.TC_NOTE, { noteIndex: parseInt(index, 10) + 1 }),
  width: 180,
  filter: 'agSetColumnFilter',
  hide: true
})

const NOTE_COLUMNS = NOTE_INDEXES.map(makeNoteColumn)

const UPLOAD_ROW_INDEX: ColDef = {
  colId: LiveGridColumnId.UPLOAD_ROW_INDEX,
  headerName: Locale.translate(Translation.TC_UPLOAD_ROW_INDEX),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.uploadRowIndex,
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const IS_CURVE = {
  colId: LiveGridColumnId.NEGOTIATION_IS_CURVE,
  headerName: Locale.translate(Translation.TC_CURVE),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.isCurve,
  valueFormatter: booleanFormatter,
  width: 70,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_FEE = {
  colId: LiveGridColumnId.NEGOTIATION_FEE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && rowData.negotiation.fee && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.fee))
  },
  valueFormatter: makeFeeFormatter(),
  headerName: Locale.translate(Translation.TC_FEE),
  equals: () => false,
  width: 80,
  minWidth: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  headerClass: 'group-header-cell',
  cellClass: 'font-weight-bold',
  filterValueGetter: makeRateFilterGetter(),
  getQuickFilterText: makeRateQuickFilterTextGetter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getRateModificationType(negotiation.fee),
    validationField: NegotiationFieldNames.FEE,
    isRateField: true,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: canEditNegotiationDetailFee,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: (negotiationDetail: NegotiationDetail) => ({
      formatOptions: getFeeFormatOptions(),
      updateOnBlurOnly: negotiationDetail.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY,
      dataTestId: 'LiveNegotiationsGridModel-RateNumericInput'
    }),
    rowDataGetter: negotiationRowDataGetter,
    getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
    defaultField: NegotiationFieldNames.FEE_NEGOTIATED,
    updateField: NegotiationFieldNames.FEE_MODIFIED,
    isModifiedField: NegotiationFieldNames.FEE_IS_MODIFIED,
    validationField: NegotiationFieldNames.FEE,
    useEnterForNavigation: true
  },
  lockPinned: true,
  enableRowGroup: false
}

const SECONDARY_COLLATERAL_NAME: ColDef = {
  colId: LiveGridColumnId.SECONDARY_COLLATERAL_NAME,
  headerName: Locale.translate(Translation.TC_SECONDARY_COLLATERAL),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.secondaryInstrumentName,
  width: 160,
  minWidth: 160,
  filter: 'agSetColumnFilter',
  equals: () => false,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.SECONDARY_BASKET_ID,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: InitiatedSecondaryCollateralCombobox,
    inputParams: (negotiationDetail: NegotiationDetail) => ({
      detail: negotiationDetail,
      withPortal: true
    }),
    defaultField: NegotiationFieldNames.SECONDARY_BASKET_ID_NEGOTIATED,
    updateField: NegotiationFieldNames.SECONDARY_BASKET_ID_MODIFIED,
    isModifiedField: NegotiationFieldNames.SECONDARY_BASKET_ID_IS_MODIFIED,
    validationField: NegotiationFieldNames.SECONDARY_BASKET_ID
  },
  editable: canEditNegotiationDetailSecondaryCollateral,
  lockPinned: true
}

const SBL_LENDING_TYPE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_STOCK_BORROWING_LENDING_TYPE,
  headerName: Locale.translate(Translation.TC_STOCK_BORROW_LENDING_TYPE),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const lendingType = negotiationRowDataGetter(params)?.negotiation.stockBorrowingLendingType
    return lendingType && getNegotiatedValue(lendingType)
  },
  width: 140,
  filter: 'agSetColumnFilter',
  equals: () => false,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.STOCK_BORROW_LENDING_TYPE,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: false,
  lockPinned: true
}

const LINKED_TRADE_ID: ColDef = {
  colId: LiveGridColumnId.LINKED_TRADE_ID,
  headerName: Locale.translate(Translation.TC_LINKED_TRADE_ID),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter<NegotiationDetail>(params)
    return isNotNil(rowData) ? getTargetValueIfNotSummary(rowData, rowData.linkedTradeId) : null
  },
  width: 140,
  filter: 'agSetColumnFilter',
  equals: () => false,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.LINKED_TRADE_ID,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: false,
  lockPinned: true,
  hide: true
}

const MY_ENTITY_SHORT_NAME: ColDef = {
  colId: LiveGridColumnId.MY_ENTITY_SHORT_NAME,
  headerName: Locale.translate(Translation.TC_MY_ENTITY_SHORT_NAME),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
    negotiationRowDataGetter<NegotiationDetail>(params)?.orderFund?.shortName,
  width: 150,
  filter: 'agSetColumnFilter',
  equals: () => false,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.MY_ENTITY_SHORT_NAME,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: false,
  lockPinned: true,
  hide: true
}

const MY_FICC_ENTITY_SHORT_NAME: ColDef = {
  colId: LiveGridColumnId.MY_FICC_ENTITY_SHORT_NAME,
  headerName: Locale.translate(Translation.TC_MY_FICC_ENTITY_SHORT_NAME),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
    negotiationRowDataGetter<NegotiationDetail>(params)?.orderFund?.ficcShortName,
  width: 150,
  filter: 'agSetColumnFilter',
  editable: false,
  lockPinned: true,
  hide: true
}

const ROE: ColDef = {
  ...BASE_ROE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.rightOfExit)
  },
  editable: false,
  hide: true
}

const ROS: ColDef = {
  ...BASE_ROS,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.rightOfSubstitution)
  },
  editable: false,
  hide: true
}

const MARKET_TYPE: ColDef = {
  ...BASE_MARKET_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, negotiationRowDataGetter(params)?.negotiation.marketType)
  },
  hide: true
}

const MIC: ColDef = {
  ...BASE_MIC,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, negotiationRowDataGetter(params)?.negotiation?.marketIdentifierCode)
  },
  hide: true
}
const ISSUE: ColDef = {
  ...BASE_ISSUE,
  colId: LiveGridColumnId.ISSUE,
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
    negotiationRowDataGetter<NegotiationDetail>(params)?.negotiation.collateralIssue,
  equals: () => false,
  editable: false,
  lockPinned: true,
  hide: true
}

const MARKET_TRADE_DATE: ColDef = {
  colId: LiveGridColumnId.MARKET_TRADE_DATE,
  headerName: Locale.translate(Translation.TC_MARKET_TRADE_DATE),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    return negotiationRowDataGetter<NegotiationDetail>(params)?.negotiation.marketTradeDate
  },
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeDateFormatter()
}

const VISIBLE_COLUMNS: (ColGroupDef | ColDef)[] = [
  CHECKBOX,
  GROUPING,
  ACTIONS,
  createMasterDetailRowColDef({
    isRowMaster: (negotiationDetail: NegotiationDetail) => hasNegotiationNestedLegs(negotiationDetail)
  }),
  NEGOTIATION_STATUS,
  COLLECTION_NAME,
  COUNTERPARTY_NAME,
  RFQ_TIMER,
  FUND_NAME,
  NEGOTIATION_DIRECTION,
  COLLATERAL_NAME,
  SECONDARY_COLLATERAL_NAME,
  SBL_LENDING_TYPE,
  NEGOTIATION_NOTIONAL_GROUP,
  NEGOTIATION_RATE_GROUP,
  NEGOTIATION_FEE,
  NEGOTIATION_HAIRCUT_TYPE,
  NEGOTIATION_HAIRCUT_GROUP,
  NEGOTIATION_START_DATE_GROUP,
  NEGOTIATION_END_DATE_GROUP,
  MARKET_TRADE_DATE,
  NEGOTIATION_TERM,
  NEGOTIATION_START_CONSIDERATION_GROUP,
  NEGOTIATION_ACCRUED_INTEREST,
  NEGOTIATION_BOND_PRICE,
  NEGOTIATION_ALL_IN_PRICE,
  NEGOTIATION_END_CONSIDERATION,
  INVESTMENT_DECISION_MAKER,
  EXECUTION_DECISION_MAKER,
  OTW_DURATION,
  NEGOTIATION_AXE_ROLL_UNWIND,
  COLLATERAL_ISIN_OR_BUI,
  COLLATERAL_CURRENCY,
  NEGOTIATION_REPO_TYPE,
  NEGOTIATION_RFQ_TYPE
]

const HIDDEN_COLUMNS: (ColGroupDef | ColDef)[] = [
  COUNTERPARTY_CLEARING,
  COUNTERPARTY_SPONSOR,
  EXECUTING_COUNTERPARTY_NAME,
  NEGOTIATION_LAST_RESPONDER,
  NEGOTIATION_UPDATED_AT_DATE,
  NEGOTIATION_UPDATED_AT_TIME,
  COLLATERAL_BASKET_DESCRIPTION,
  COLLATERAL_BASKET_DBV_CLASS,
  NEGOTIATION_CALLABLE,
  NEGOTIATION_CALL_DAYS,
  COUNTERPARTY_FIRM,
  REFERENCE_RATE_NAME,
  FLOAT_CRYSTALLISATION_DAYS,
  NEGOTIATION_SETTLEMENT_VENUE,
  NEGOTIATION_CSD,
  PRODUCT,
  NEGOTIATION_ORDER_ID,
  NEGOTIATION_AXE_ID,
  NEGOTIATION_ASSET_TYPE,
  NEGOTIATION_FACTOR,
  NEGOTIATION_LAST_INITIATOR,
  NEGOTIATION_CREATED_AT_DATE,
  NEGOTIATION_CREATED_AT_TIME,
  NEGOTIATION_UPDATED_BY,
  SUBMITTING_USER,
  COLLECTION_TYPE,
  ABSOLUTE_CONSIDERATION,
  TOTAL_FILL_NOTIONAL,
  TARGET_FILL_NOTIONAL,
  TOTAL_FILL_START_CONSIDERATION,
  TARGET_FILL_START_CONSIDERATION,
  NEGOTIATION_TRADE_TYPE,
  CLEARING_HOUSE,
  COUNTERPARTY_LEI,
  COUNTERPARTY_BIC,
  EVERGREEN_NOTICE_PERIOD,
  COUNTERPARTY_FIRM_LEI,
  ORDER_PREV_RATE,
  COLLATERAL_COUPON,
  NEGOTIATION_REPO_INTEREST,
  BOOK_NAME,
  STRATEGY_NAME,
  INITIATOR_PRIME_BROKER_NAME,
  BUNDLE_NAME,
  COLLATERAL_ISSUER,
  BLOCK_TRADE,
  FUND_LEI,
  FUND_BIC,
  FUND_FIRM,
  FUND_FIRM_LEI,
  COLLATERAL_CUSIP,
  PRODUCT_SECTOR,
  NEGOTIATION_MARKET,
  COLLECTION_ID,
  CONTAINER_COLLECTION_NAME,
  CONTAINER_COLLECTION_ID,
  NOTICE_PERIOD_DAYS,
  ...NOTE_COLUMNS,
  UPLOAD_ROW_INDEX,
  ALLOW_PARTIAL,
  NEGOTIATION_PRE_AGREED,
  NEGOTIATION_MASTER_LEGAL_AGREEMENT,
  NEGOTIATION_ID,
  IS_CURVE,
  LINKED_TRADE_ID,
  MY_ENTITY_SHORT_NAME,
  MY_FICC_ENTITY_SHORT_NAME,
  ISSUE,
  ROE,
  ROS,
  MARKET_TYPE,
  MIC
].sort((a, b) => (a.headerName && b.headerName ? a.headerName.localeCompare(b.headerName) : 0))

export const nonCounterpartyColumnDefs = VISIBLE_COLUMNS.concat(HIDDEN_COLUMNS)

export const commonGridLeftColumnDefs: (ColGroupDef | ColDef)[] = [
  GROUPING,
  COUNTERPARTY_ACTIONS,
  {
    ...COLLECTION_NAME,
    enableRowGroup: true,
    rowGroup: true,
    hide: true
  },
  OTW_TIMER_SELECTED_COUNTERPARTY,
  COUNTERPARTY_AXE_ROLL_UNWIND_SUMMARY,
  COUNTERPARTY_RESPONSE_COUNT,
  RFQ_TIMER,
  SBL_LENDING_TYPE,
  COLLATERAL_NAME,
  ISSUE,
  NEGOTIATION_DIRECTION,
  COLLATERAL_CCY,
  NEGOTIATION_NOTIONAL_ORIGINAL_COMMON,
  MARKET_TRADE_DATE,
  SUBMITTING_USER,
  ORDER_PREV_RATE,
  BEST_QUOTE_GROUP,
  EVERGREEN_NOTICE_PERIOD
]

const counterpartyGridVisibleColumnDefs: (ColGroupDef | ColDef)[] = [
  COUNTERPARTY_CHECKBOX,
  COUNTERPARTY_NEGOTIATION_STATUS,
  COUNTERPARTY_NEGOTIATION_TIMER,
  NEGOTIATION_TERM,
  NEGOTIATION_RATE_GROUP,
  {
    ...NEGOTIATION_REPO_INTEREST,
    hide: false
  },
  NEGOTIATION_START_CONSIDERATION,
  NEGOTIATION_ALL_IN_PRICE,
  NEGOTIATION_START_CONSIDERATION_GROUP_CPTY,
  NEGOTIATION_FEE,
  SECONDARY_COLLATERAL_NAME,

  NEGOTIATION_HAIRCUT_TYPE,
  NEGOTIATION_HAIRCUT_GROUP,
  NEGOTIATION_NOTIONAL_GROUP,
  NEGOTIATION_RFQ_TYPE,

  NEGOTIATION_START_DATE_GROUP,
  NEGOTIATION_END_DATE_GROUP,

  NEGOTIATION_BOND_PRICE,
  NEGOTIATION_END_CONSIDERATION,
  NEGOTIATION_REPO_TYPE,
  NEGOTIATION_AXE_ROLL_UNWIND
]

const counterpartyGridHiddenColumnDefs: (ColGroupDef | ColDef)[] = [
  ABSOLUTE_CONSIDERATION,
  NEGOTIATION_LAST_RESPONDER,
  NEGOTIATION_LAST_INITIATOR,
  NEGOTIATION_UPDATED_BY,
  NEGOTIATION_UPDATED_AT_DATE,
  NEGOTIATION_UPDATED_AT_TIME,
  COUNTERPARTY_FIRM,
  COUNTERPARTY_LEI,
  COUNTERPARTY_BIC,
  COUNTERPARTY_FIRM_LEI,
  NEGOTIATION_PRE_AGREED,
  NEGOTIATION_MASTER_LEGAL_AGREEMENT,
  NEGOTIATION_ID,
  NEGOTIATION_AXE_ID,
  BOOK_NAME,
  STRATEGY_NAME,
  LINKED_TRADE_ID,
  MY_ENTITY_SHORT_NAME,
  MY_FICC_ENTITY_SHORT_NAME,
  ROE,
  ROS,
  MARKET_TYPE,
  MIC,
  NEGOTIATION_FACTOR,
  NEGOTIATION_ASSET_TYPE
].map(colDef => ({
  ...colDef,
  hide: true
}))

export const bestPriceCellClassHandler: CellClassFunc<NegotiationDetail> = ({ context, data }) => {
  if (
    ![
      NegotiationType.CONTINGENT_ITEM,
      NegotiationType.ONE_CANCELS_OTHERS_ITEM,
      NegotiationType.CONTINGENT_SUMMARY,
      NegotiationType.ONE_CANCELS_OTHERS_SUMMARY
    ].includes(data?.negotiation.negotiationType!) ||
    !context.originalParams
  ) {
    return ''
  }

  const negotiationsToCompare = getNegotiationDetailsForBestPriceComparison(context.originalParams)
  return isNotNil(negotiationsToCompare) && isEqual(getBestQuoteReceived(negotiationsToCompare), data)
    ? 'ag-cell--best-quote-group selected-best-price-type'
    : ''
}

export const counterpartyGridColumnDefs = counterpartyGridVisibleColumnDefs.concat(counterpartyGridHiddenColumnDefs).map(
  createNestedColumnMapper(c => ({
    ...c,
    cellClass: bestPriceCellClassHandler
  }))
)

const commonGridRightVisibleColumnDefs: (ColGroupDef | ColDef)[] = [INVESTMENT_DECISION_MAKER, EXECUTION_DECISION_MAKER, FUND_NAME]

const commonGridRightHiddenColumnDefs: (ColGroupDef | ColDef)[] = [
  TOTAL_FILL_NOTIONAL,
  TARGET_FILL_NOTIONAL,
  TOTAL_FILL_START_CONSIDERATION,
  TARGET_FILL_START_CONSIDERATION,
  NEGOTIATION_TRADE_TYPE,
  NEGOTIATION_ORDER_ID,
  NEGOTIATION_CREATED_AT_DATE,
  NEGOTIATION_CREATED_AT_TIME,
  COLLECTION_TYPE,
  COLLECTION_ID,
  NOTICE_PERIOD_DAYS,
  PRODUCT,
  PRODUCT_SECTOR,
  COLLATERAL_ISIN_OR_BUI,
  REFERENCE_RATE_NAME,
  FLOAT_CRYSTALLISATION_DAYS,
  NEGOTIATION_SETTLEMENT_VENUE,
  COUNTERPARTY_CLEARING,
  COUNTERPARTY_SPONSOR,
  CLEARING_HOUSE,
  COLLATERAL_BASKET_DESCRIPTION,
  COLLATERAL_BASKET_DBV_CLASS,
  NEGOTIATION_CALLABLE,
  NEGOTIATION_CALL_DAYS,
  NEGOTIATION_ACCRUED_INTEREST,
  NEGOTIATION_CSD,
  COLLATERAL_COUPON,
  INITIATOR_PRIME_BROKER_NAME,
  BUNDLE_NAME,
  COLLATERAL_ISSUER,
  BLOCK_TRADE,
  FUND_LEI,
  FUND_BIC,
  FUND_FIRM,
  FUND_FIRM_LEI,
  COLLATERAL_CUSIP,
  PRODUCT_SECTOR,
  NEGOTIATION_MARKET,
  ...NOTE_COLUMNS,
  UPLOAD_ROW_INDEX,
  ALLOW_PARTIAL,
  IS_CURVE
].map(colDef => ({
  ...colDef,
  hide: true,
  rowGroup: false
}))

export const commonGridRightColumnDefs = [
  {
    marryChildren: true,
    children: commonGridRightVisibleColumnDefs.concat(commonGridRightHiddenColumnDefs)
  }
]
